import React, { useMemo, useState } from 'react';
import { isSameDay } from 'date-fns';
import PropTypes from 'prop-types';

import { pluralize } from '@chronos/utils';
import Button from '@chronos/components/Button';
import BillingIcon from '@chronos/components/Icons/BillingIcon';
import ExternalLInkIcon from '@chronos/components/Icons/ExternalLinkIcon';
import config from 'app-config';

import ItemPlaceholder from './ItemPlaceholder';
import TransactionItemDetails from '../TransactionItemDetails';
import * as S from './styled';

const dateTimeIntl = new Intl.DateTimeFormat([], {
  dateStyle: 'medium',
});

const IntlDate = ({ date }) => {
  const formatted = useMemo(() => {
    return dateTimeIntl.format(new Date(date));
  }, [date]);

  return <div>{formatted}</div>;
};
IntlDate.propTypes = {
  date: PropTypes.instanceOf(Date),
};

const currencyEmojiMap = {
  USD: '💵',
  EUR: '💶',
};

export const isDailyFirstItem = ({ index, transactions, listItems }) => {
  if (index === 0) {
    return true;
  }

  const transaction = transactions[listItems[index]] ?? listItems[index];
  // Undefined behavior for unloaded entry
  if (!transaction) {
    return false;
  }

  const prevTransaction =
    transactions[listItems[index - 1]] || listItems[index - 1];

  if (!prevTransaction) {
    return false;
  }

  return !isSameDay(
    transaction.computed.createdAt,
    prevTransaction.computed.createdAt
  );
};

const TransactionItem = ({ index, style, data }) => {
  const {
    transactions,
    listItems,
    dayPoints,
    onResizeDetailsArea,
    onClickExpandTransaction,
    isTransactionDetailsExpanded,
  } = data;
  const transaction = transactions[listItems[index]] || listItems[index];
  if (!transaction) {
    return (
      <div style={style}>
        <ItemPlaceholder index={index} />
      </div>
    );
  }

  const [showButtons, setShowButtons] = useState(false);
  const onMouseEnter = () => {
    setShowButtons(true);
  };
  const onMouseLeave = () => {
    setShowButtons(false);
  };

  const showTimeline = isDailyFirstItem({
    index,
    transactions,
    listItems,
  });

  const { currency } = transaction.attributes.data;

  return (
    <div
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {showTimeline && (
        <S.Timeline>
          <IntlDate date={transaction.computed.createdAt} />
          <div style={{ marginLeft: '4px' }}>
            {dayPoints?.[transaction.computed.createdAtDayStr]
              ? [
                  '- ',
                  dayPoints[
                    transaction.computed.createdAtDayStr
                  ].income.toFixed(2),
                  ` ${currency}, `,
                  dayPoints[transaction.computed.createdAtDayStr].qty,
                  pluralize(
                    dayPoints[transaction.computed.createdAtDayStr].qty,
                    {
                      one: ' client',
                      other: ' clients',
                    }
                  ),
                ].join('')
              : null}
          </div>
        </S.Timeline>
      )}
      <S.TransactionItem>
        <S.Body>
          <S.Title>
            <S.UserName>{transaction.attributes.data.legalName}</S.UserName>
            <S.ActionText>{transaction.attributes.data.mode}</S.ActionText>
            <S.ActionText bold>{transaction.attributes.data.qty}</S.ActionText>
            <S.ActionText>
              {pluralize(transaction.attributes.data.qty, {
                one: 'license ',
                other: 'licenses ',
              })}
              for
            </S.ActionText>
            <S.ActionText bold>
              {transaction.attributes.data.periodDays}
            </S.ActionText>
            <S.ActionText>
              {pluralize(transaction.attributes.data.periodDays, {
                one: 'day ',
                other: 'days ',
              })}
            </S.ActionText>
            {showButtons && (
              <S.ManagePanel>
                <S.ExpandButton
                  style={{
                    marginRight: '8px',
                  }}
                  onClick={() => {
                    onClickExpandTransaction(index);
                  }}
                >
                  …
                </S.ExpandButton>
                <Button
                  style={{
                    marginRight: '8px',
                  }}
                  appearance="subtle-link"
                  spacing="none"
                  onClick={() => {
                    window.open(
                      [
                        config.chronosApiUrl,
                        'trello/invoice',
                        transaction.attributes.id,
                        transaction.attributes.paymentProvider === 'every-pay'
                          ? 'e'
                          : transaction.attributes.paymentProvider,
                      ].join('/'),
                      '_blank'
                    );
                  }}
                  withIcon
                >
                  <BillingIcon size="middle" />
                </Button>
                <Button
                  style={{
                    marginRight: '12px',
                  }}
                  appearance="subtle-link"
                  spacing="none"
                  onClick={() => {
                    if (transaction.attributes.paymentProvider === 'stripe') {
                      window.open(
                        [
                          'https://dashboard.stripe.com/payments/',
                          transaction.attributes.data.payment.payment_intent,
                        ].join(''),
                        '_blank'
                      );
                    }
                    if (
                      transaction.attributes.paymentProvider === 'every-pay'
                    ) {
                      window.open(
                        [
                          'https://portal.every-pay.eu/payments/',
                          transaction.attributes.data.payment.payment_reference,
                        ].join(''),
                        '_blank'
                      );
                    }
                  }}
                  withIcon
                >
                  <ExternalLInkIcon size="middle" />
                </Button>
              </S.ManagePanel>
            )}
          </S.Title>
          <S.Title>
            <S.ActionText bold>
              {currencyEmojiMap[currency]}{' '}
              {(
                transaction.attributes.data.totalWithDiscount +
                transaction.attributes.data.vatAmount
              ).toFixed(2)}{' '}
              {currency}
            </S.ActionText>
            {transaction.attributes.data.vatAmount !== 0 && (
              <S.ActionText>👉 Included ％VAT</S.ActionText>
            )}
            {transaction.attributes.data.vatAmount !== 0 && (
              <S.ActionText bold>
                {transaction.attributes.data.vatAmount} {currency}
              </S.ActionText>
            )}
            <S.ActionText>from</S.ActionText>
            <S.ActionText bold>
              {transaction.attributes.data.isEU ? '€ ' : '🌎 '}
              {transaction.attributes.data.country}
            </S.ActionText>
            <S.ActionText>
              {transaction.attributes.data.legalAddress}
            </S.ActionText>
          </S.Title>
          <TransactionItemDetails
            isExpanded={isTransactionDetailsExpanded(index)}
            onResize={(size) => {
              onResizeDetailsArea(index, size);
            }}
            transaction={transaction}
          />
          <S.Footer>
            <S.FooterInfo>
              <div>
                {transaction.computed.createdAtDayStr}{' '}
                {transaction.computed.createdAtTimeStr} -{' '}
                {transaction.computed.finishedAtDayStr}
              </div>
            </S.FooterInfo>
          </S.Footer>
          <div style={{ marginLeft: '8px' }}>
            <S.Divider />
          </div>
        </S.Body>
      </S.TransactionItem>
    </div>
  );
};

TransactionItem.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    transactions: PropTypes.object.isRequired,
    listItems: PropTypes.object.isRequired,
    dayPoints: PropTypes.object,
    isTransactionDetailsExpanded: PropTypes.func.isRequired,
    onResizeDetailsArea: PropTypes.func.isRequired,
    onClickExpandTransaction: PropTypes.func.isRequired,
  }).isRequired,
};

export default TransactionItem;
